import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
// material
import { styled, useTheme } from '@mui/material/styles';
// hooks
import useAuth from 'hooks/useAuth';
import { createPartnerUsersApi } from 'redux/analytics/analyticsApi';
import { useDispatch } from 'react-redux';
import useResponsive from 'hooks/useResponsive';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
//
import DashboardNavbar from './DashboardNavbar';
import DashboardSidebar from './DashboardSidebar';
import FooterNavbar from './FooterNavbar';
import HeaderNavbar from './HeaderNavbar';
// ----------------------------------------------------------------------

const APP_BAR_MOBILE = 36;
const APP_BAR_DESKTOP = 92;

const RootStyle = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden'
});

const MainStyle = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100%',
  paddingTop: APP_BAR_MOBILE,
  paddingBottom: theme.spacing(10),
  [theme.breakpoints.up('lg')]: {
    paddingTop: 24,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1)
  }
}));

// ----------------------------------------------------------------------

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const theme = useTheme();
  const { collapseClick } = useCollapseDrawer();
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const isDesktop = useResponsive('up', 'md');

  useEffect(() => {
    const storeStartTime = async () => {
      try {
        const startTime = new Date();
        await localStorage.setItem('AppStartTime', startTime.toISOString()); // Serialize Date to ISO string
        await dispatch(
          createPartnerUsersApi({
            module: 'SCREEN_MODE',
            event: 'ONLINE',
            message: 'Screen Monitor',
            startTime: startTime.toISOString(),
            moduleInformation: user?.userId,
            // endTime: 'Fri Jul 05 2024 14:23:59 GMT+0530',
            // totalTimeDuration: 200,
            platform: 'ADMIN_PAGE',
            phoneNumber: user?.contactInfo?.phoneNumber?.primary || ''
          })
        );
        console.log('App start time stored:', startTime);
      } catch (error) {
        console.error('Error saving start time:', error);
      }
    };

    const storeEndTime = async () => {
      try {
        const endTime = new Date();
        await localStorage.setItem('AppEndTime', endTime.toISOString()); // Serialize Date to ISO string

        // Retrieve start time and end time from localStorage
        const startingTimeStr = await localStorage.getItem('AppStartTime');
        const endingTimeStr = await localStorage.getItem('AppEndTime');

        if (startingTimeStr && endingTimeStr) {
          const startingTime = new Date(startingTimeStr); // Deserialize ISO string to Date
          const endingTime = new Date(endingTimeStr); // Deserialize ISO string to Date

          console.log('Starting Time:', startingTime);
          console.log('Ending Time:', endingTime);

          const timeDifferenceMs = endingTime - startingTime;
          console.log('Time difference in milliseconds:', timeDifferenceMs);
          await dispatch(
            createPartnerUsersApi({
              module: 'SCREEN_MODE',
              event: 'OFFLINE',
              message: 'Screen Monitor',
              startTime: startingTime,
              moduleInformation: user?.userId,
              endTime: endingTime,
              totalTimeDuration: timeDifferenceMs,
              platform: 'ADMIN_PAGE',
              phoneNumber: user?.contactInfo?.phoneNumber?.primary || ''
            })
          );

          // Convert milliseconds to seconds for easier reading
          // const secondsDifference = timeDifferenceMs / 1000;
          // console.log('Time difference in seconds:', secondsDifference);
        }

        console.log('App end time stored:', endTime);
      } catch (error) {
        console.error('Error saving end time:', error);
      }
    };

    const handleAppStateChange = (nextAppState) => {
      console.log(nextAppState, 'nextAppState');
      if ((nextAppState?.type === 'online' || nextAppState?.type === 'pageshow') && user?.role === 'OEM') {
        storeStartTime();
      } else if ((nextAppState?.type === 'offline' || nextAppState?.type === 'pagehide') && user?.role === 'OEM') {
        storeEndTime();
      }
    };
    window.addEventListener('online', handleAppStateChange);
    window.addEventListener('offline', handleAppStateChange);
    window.addEventListener('pageshow', handleAppStateChange);
    window.addEventListener('pagehide', handleAppStateChange);

    return () => {
      window?.removeEventListener('online', handleAppStateChange); // cleanup
      window?.removeEventListener('offline', handleAppStateChange);
      window?.removeEventListener('pageshow', handleAppStateChange);
      window?.removeEventListener('pagehide', handleAppStateChange);
    };
  }, []);

  return (
    <RootStyle>
      <DashboardNavbar onOpenSidebar={() => setOpen(true)} />
      <DashboardSidebar user={user} logout={logout} isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />
      <MainStyle
        sx={{
          transition: theme.transitions.create('margin', {
            duration: theme.transitions.duration.complex
          }),
          ...(collapseClick && {
            ml: '102px'
          })
        }}
      >
        {isDesktop && <HeaderNavbar />}
        <Outlet />
        {/* {user?.role === 'OEM' && <FooterNavbar />} */}
        <FooterNavbar />
      </MainStyle>
    </RootStyle>
  );
}
